import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Textarea from '../../../components/ui/Textarea';
import { PROFILE_CONSTANTS } from '../constants/profileConstants';

const ProfessionalTab = ({ 
  profileData, 
  fieldErrors, 
  onInputChange 
}) => {
  // State to track local validation errors
  const [localErrors, setLocalErrors] = useState({});
  
  // Function to validate rates when either changes
  const validateRates = (minRate, maxRate) => {
    const min = parseFloat(minRate);
    const max = parseFloat(maxRate);
    
    // Clear previous local errors
    const newErrors = { ...localErrors };
    delete newErrors.minimum_rate;
    delete newErrors.maximum_rate;
    
    // Only validate if both values are provided and are valid numbers
    if (!isNaN(min) && !isNaN(max) && min > max) {
      newErrors.minimum_rate = 'Minimum rate cannot be higher than maximum rate';
      newErrors.maximum_rate = 'Maximum rate cannot be lower than minimum rate';
    }
    
    setLocalErrors(newErrors);
  };
  
  // Custom handler for rate changes
  const handleRateChange = (field, value) => {
    // Update the field value
    onInputChange(field, value);
    
    // Get the other rate value for comparison
    const minRate = field === 'minimum_rate' ? value : profileData.minimum_rate;
    const maxRate = field === 'maximum_rate' ? value : profileData.maximum_rate;
    
    // Validate rates
    validateRates(minRate, maxRate);
  };
  
  // Validate rates on component mount and when profileData changes
  useEffect(() => {
    validateRates(profileData.minimum_rate, profileData.maximum_rate);
  }, [profileData.minimum_rate, profileData.maximum_rate]);
  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-1">Professional Information</h2>
        <p className="text-text-secondary text-sm">Share your work experience and professional details</p>
      </div>

      {/* Experience and Rates */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
        <h3 className="text-lg font-semibold text-text-primary mb-6">Experience & Rates</h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <Input
            label="Years of Experience"
            type="number"
            required
            value={profileData.experience_years || ''}
            onChange={(e) => onInputChange('experience_years', e.target.value)}
            placeholder="Number of years"
            error={fieldErrors.experience_years}
            min="0"
            max="50"
            step="1"
          />
          
          <div>
            <label className="block mb-2 text-text-secondary font-medium">Experience Level</label>
            <select 
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              value={profileData.years_of_experience_category || 'entry'}
              onChange={(e) => onInputChange('years_of_experience_category', e.target.value)}
            >
              {PROFILE_CONSTANTS.EXPERIENCE_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
          </div>
          
          <Input
            label="Hourly Rate (USD)"
            type="number"
            required
            value={profileData.hourly_rate || ''}
            onChange={(e) => onInputChange('hourly_rate', e.target.value)}
            placeholder="Your hourly rate"
            error={fieldErrors.hourly_rate}
            min="0"
            step="0.01"
          />
          
          <div>
            <label className="block mb-2 text-text-secondary font-medium">Industry Specialization</label>
            <div className="mb-3">
              <select 
                className="w-full px-3 py-2 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    const selectedIndustry = e.target.value;
                    // Create a new array with the selected industry, avoiding duplicates
                    const currentIndustries = Array.isArray(profileData.industry_specialization) 
                      ? profileData.industry_specialization 
                      : profileData.industry_specialization ? [profileData.industry_specialization] : [];
                    
                    if (!currentIndustries.includes(selectedIndustry)) {
                      const updatedIndustries = [...currentIndustries, selectedIndustry];
                      onInputChange('industry_specialization', updatedIndustries);
                    }
                    e.target.value = ''; // Reset the select field
                  }
                }}
              >
                <option value="">Select industries (multiple allowed)</option>
                {PROFILE_CONSTANTS.INDUSTRY_SPECIALIZATIONS.filter(industry => 
                  !Array.isArray(profileData.industry_specialization) 
                    ? profileData.industry_specialization !== industry 
                    : !profileData.industry_specialization?.includes(industry)
                ).map((industry) => (
                  <option key={industry} value={industry}>
                    {industry}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Selected Industries */}
            <div className="flex flex-wrap gap-2">
              {Array.isArray(profileData.industry_specialization) 
                ? profileData.industry_specialization.map((industry, index) => (
                  <div
                    key={index}
                    className="bg-primary-50 text-primary-700 px-3 py-1 rounded-full text-sm flex items-center gap-2 border border-primary-200"
                  >
                    <span>{industry}</span>
                    <button
                      onClick={() => {
                        const updatedIndustries = profileData.industry_specialization.filter(i => i !== industry);
                        onInputChange('industry_specialization', updatedIndustries);
                      }}
                      className="text-primary-500 hover:text-primary-700 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ))
                : profileData.industry_specialization && (
                  <div
                    className="bg-primary-50 text-primary-700 px-3 py-1 rounded-full text-sm flex items-center gap-2 border border-primary-200"
                  >
                    <span>{profileData.industry_specialization}</span>
                    <button
                      onClick={() => onInputChange('industry_specialization', [])}
                      className="text-primary-500 hover:text-primary-700 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                )
              }
            </div>
          </div>
        </div>
        
        <div className="mt-4 text-xs text-text-secondary">
          <p>Your standard rate per hour in USD</p>
        </div>
      </div>

      {/* Rate Range */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
        <h3 className="text-lg font-semibold text-text-primary mb-6">Rate Range</h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <Input
            label="Minimum Rate (USD)"
            type="number"
            value={profileData.minimum_rate || ''}
            onChange={(e) => handleRateChange('minimum_rate', e.target.value)}
            placeholder="Minimum acceptable rate"
            error={localErrors.minimum_rate || fieldErrors.minimum_rate}
            min="0"
            step="0.01"
          />
          
          <Input
            label="Maximum Rate (USD)"
            type="number"
            value={profileData.maximum_rate || ''}
            onChange={(e) => handleRateChange('maximum_rate',  e.target.value)}
            placeholder="Maximum rate for complex projects"
            error={localErrors.maximum_rate || fieldErrors.maximum_rate}
            min="0"
            step="0.01"
          />
        </div>
        
        <div className="mt-4 text-xs text-text-secondary">
          <p>Set your rate range to help clients understand your pricing flexibility</p>
        </div>
      </div>
      
      {/* My Work Style */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
        <h3 className="text-lg font-semibold text-text-primary mb-6">My Work Style</h3>
        <p className="text-text-secondary text-sm mb-4">Help clients understand how you approach work and collaborate</p>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block mb-2 text-text-secondary font-medium">Work Pace</label>
            <select 
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              value={profileData.work_pace || 'balanced'}
              onChange={(e) => onInputChange('work_pace', e.target.value)}
            >
              {PROFILE_CONSTANTS.WORK_PACE_PREFERENCES.map((pace) => (
                <option key={pace} value={pace}>
                  {pace.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-text-secondary">Your typical speed when working on tasks</p>
          </div>
          
          <div>
            <label className="block mb-2 text-text-secondary font-medium">Feedback Preference</label>
            <select 
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              value={profileData.feedback_preference || 'regular'}
              onChange={(e) => onInputChange('feedback_preference', e.target.value)}
            >
              {PROFILE_CONSTANTS.FEEDBACK_PREFERENCES.map((preference) => (
                <option key={preference} value={preference}>
                  {preference.charAt(0).toUpperCase() + preference.slice(1)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-text-secondary">How often you prefer to receive client feedback</p>
          </div>
          
          <div>
            <label className="block mb-2 text-text-secondary font-medium">Collaboration Style</label>
            <select 
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              value={profileData.collaboration_style || 'adaptable'}
              onChange={(e) => onInputChange('collaboration_style', e.target.value)}
            >
              {PROFILE_CONSTANTS.COLLABORATION_STYLES.map((style) => (
                <option key={style} value={style}>
                  {style.charAt(0).toUpperCase() + style.slice(1)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-text-secondary">How you prefer to work with clients and teams</p>
          </div>
          
          <div>
            <label className="block mb-2 text-text-secondary font-medium">Problem Solving Approach</label>
            <select 
              className="w-full px-3 py-2 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              value={profileData.problem_solving_approach || 'analytical'}
              onChange={(e) => onInputChange('problem_solving_approach', e.target.value)}
            >
              {PROFILE_CONSTANTS.PROBLEM_SOLVING_APPROACHES.map((approach) => (
                <option key={approach} value={approach}>
                  {approach.charAt(0).toUpperCase() + approach.slice(1)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-text-secondary">Your typical approach to solving challenges</p>
          </div>
        </div>
        
        <div className="mt-6">
          <label className="block mb-2 text-text-secondary font-medium">Work Style Description</label>
          <Textarea
            rows={3}
            value={profileData.work_style_description || ''}
            onChange={(e) => onInputChange('work_style_description', e.target.value)}
            placeholder="Briefly describe your unique work style, approach to projects, and how you collaborate with clients..."
            error={fieldErrors.work_style_description}
            maxLength={300}
          />
          <div className="text-right text-xs text-text-secondary mt-1">
            {(profileData.work_style_description || '').length}/300 characters
          </div>
        </div>
      </div>

      {/* Professional Links */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
        <h3 className="text-lg font-semibold text-text-primary mb-6">Professional Links</h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <Input
            label="Website"
            type="url"
            value={profileData.website || ''}
            onChange={(e) => onInputChange('website', e.target.value)}
            placeholder="https://yourwebsite.com"
            error={fieldErrors.website}
          />
          
          <Input
            label="LinkedIn Profile"
            type="url"
            value={profileData.linkedin || ''}
            onChange={(e) => onInputChange('linkedin', e.target.value)}
            placeholder="https://linkedin.com/in/yourprofile"
            error={fieldErrors.linkedin}
          />
          
          <Input
            label="Portfolio URL"
            type="url"
            value={profileData.portfolio_url || ''}
            onChange={(e) => onInputChange('portfolio_url', e.target.value)}
            placeholder="https://yourportfolio.com"
            error={fieldErrors.portfolio_url}
          />
          
          <Input
            label="GitHub URL"
            type="url"
            value={profileData.github_url || ''}
            onChange={(e) => onInputChange('github_url', e.target.value)}
            placeholder="https://github.com/yourusername"
            error={fieldErrors.github_url}
          />
        </div>
      </div>
    </div>
  );
};

export default ProfessionalTab;

